import Sentry from '@integrations/Sentry';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button, { ButtonProps } from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import useBreakpoints from '@ui/hooks/useBreakpoints';
import IconFullArrowRight from '@ui/icons/imaterial/arrows/Full Arrow Right.svg';
import IconPin from '@ui/icons/imaterial/base/Pin 01.svg';
import theme from '@ui/theme';
import parse from 'autosuggest-highlight/parse';
import * as geofire from 'geofire-common';
import throttle from 'lodash/throttle';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import Props from '../location-search/LocationSearch.props';
import useFancyInputStyles from './FancyInput.style';

interface FancyLocationSearchClasses {
  autoComplete?: string;
  submitButton?: string;
  textField?: string;
  form?: string;
}

interface FancyLocationSearchProps extends Props {
  onSubmit?: (e: React.FormEvent<HTMLFormElement>, home: guesthouse.Location, place: google.maps.places.PlaceResult, unitValue?: string) => void;
  defaultLocation?: guesthouse.Location;
  placeholder?: string;
  autocompleteRequestOptions?: Partial<google.maps.places.AutocompletionRequest>;
  classes?: Partial<FancyLocationSearchClasses>;
  renderButton?: (props: Partial<ButtonProps>) => React.ReactNode;
  includeUnit?: boolean;
  defaultUnitNumber?: number;
}

const FancyLocationSearch: React.FC<FancyLocationSearchProps> = (props: FancyLocationSearchProps) => {
  const classNames = props.classes;
  const { classes, cx } = useFancyInputStyles({ align: props.align });
  const { md } = useBreakpoints();
  const inputRef = useRef();

  useEffect(() => setInputValue(props.defaultValue), [props.defaultValue]);

  const [inputValue, setInputValue] = useState(props.defaultValue);
  const [unitValue, setUnitValue] = useState<string>();
  const [home, setHome] = useState<guesthouse.Location>(props.defaultLocation);
  const [placeResult, setPlaceResult] = useState<google.maps.places.PlaceResult>();

  const defaultOptions = props.defaultValue
    ? [{ description: props.defaultValue }] as google.maps.places.AutocompletePrediction[]
    : [];

  const autocompleteService = useRef<google.maps.places.AutocompleteService>();
  const [options, setOptions] = useState<google.maps.places.AutocompletePrediction[]>(defaultOptions);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  const autoCompleteChange = (event: React.ChangeEvent<HTMLInputElement>, value: google.maps.places.AutocompletePrediction) => {
    if (value) {
      new window.google.maps.places.PlacesService(document.createElement('div'))
        .getDetails(
          {
            placeId: value.place_id
          },
          (result) => {
            const _geoloc = result.geometry.location.toJSON();
            const home = {
              name: result.name,
              place_id: value.place_id,
              address: value.description,
              vicinity: result.vicinity,
              _geoloc: _geoloc,
              geohash: geofire.geohashForLocation([
                _geoloc.lat,
                _geoloc.lng,
              ])
            };

            setPlaceResult(result);
            setInputValue(result.formatted_address);
            setHome(home);
            props.onLocationChange(home);
          }
        );
    } else {
      const home = {
        name: null,
        place_id: null,
        address: null,
        vicinity: null,
        _geoloc: null,
        geohash: null,
      };

      setPlaceResult(null);
      setHome(home);
      props.onLocationChange(null);
    }
  };

  const getResults = useMemo(
    () =>
      throttle((input, callback) => {
        if (input?.input) {
          try {
            autocompleteService.current.getPlacePredictions(input, callback);
          } catch (e) {
            Sentry.captureException(e);
          }
        }
      }, 200),
    [],
  );

  useEffect(() => {
    let active = true;

    if (typeof window.google === 'undefined') {
      return;
    }

    if (!autocompleteService.current) {
      try {
        autocompleteService.current = new google.maps.places.AutocompleteService();
      } catch (e) {
        Sentry.captureException(e);
        return;
      }
    }

    if (inputValue === '') {
      setOptions([]);
      return undefined;
    }

    getResults({ input: inputValue, ...props.autocompleteRequestOptions }, (results?: google.maps.places.AutocompletePrediction[]) => {
      if (active) {
        setOptions(results || []);
      }
    });

    return () => {
      active = false;
    };
  }, [inputValue, getResults]);

  return (
    <form
      className={cx(classes.inputWrapper, classNames.form)}
      style={{
        display: 'flex',
        flexDirection: md ? 'row' : 'column',
        height: !md ? 100 : 64,
        paddingTop: !md ? theme.spacing(1) : 0,
        maxWidth: md ? 625 : 500
      }}
      onSubmit={(e) => props.onSubmit && props.onSubmit(e, home, placeResult, unitValue)}
    >
      <Autocomplete
        autoComplete
        includeInputInList
        freeSolo
        className={cx(classes.autoComplete, classNames.autoComplete)}
        classes={{
          endAdornment: classes.endAdornment,
        }}
        style={props.style}
        id={props.id}
        getOptionLabel={(option: google.maps.places.AutocompletePrediction) => option.description}
        filterOptions={x => x}
        options={options}
        disabled={props.disabled}
        defaultValue={props.defaultValue && defaultOptions[0]}
        renderInput={params => {
          return (
            <TextField
              {...params}
              fullWidth
              autoFocus={props.autoFocus}
              margin="none"
              variant="standard"
              label={props.label}
              value={inputValue}
              placeholder={props.placeholder}
              inputProps={{
                id: 'location-search',
                name: 'location-search',
                ...params.inputProps,
                autoComplete: 'off',
                className: cx(classes.input, classes.locationInput, classNames.textField),
                'data-test': 'fancy-location-search-input',
              }}
              InputProps={{
                ...params.InputProps,
                className: '',
                margin: 'none',
                disableUnderline: true,
                startAdornment: <IconPin className={classes.startAdornment} />
              }}
              onChange={handleChange}
            />
          );
        }}
        renderOption={(props, option) => {
          let matches, parts;
  
          try {
            matches = option.structured_formatting.main_text_matched_substrings;
            parts = parse(
              option.structured_formatting.main_text,
              matches.map((match: any) => [match.offset, match.offset + match.length]),
            );
          } catch (e) {
            return null;
          }
  
          return (
            <li {...props}>
              <Box>
                <Typography
                  variant="body2"
                >
                  {parts.map((part, index) => (
                    <span
                      key={index}
                      style={{ fontWeight: part.highlight ? 700 : 400 }}
                    >
                      {part.text}
                    </span>
                  ))}
                </Typography>
  
                <Typography
                  variant="body2"
                  color="textSecondary"
                >
                  {option.structured_formatting.secondary_text}
                </Typography>
              </Box>
            </li>
          );
        }}
        onChange={autoCompleteChange}
      />

      <input
        ref={inputRef}
        name="unit-number"
        id="unit-number"
        placeholder="Unit Number"
        type="number"
        className={classes.input}
        value={unitValue}
        defaultValue={props.defaultUnitNumber}
        data-test="fancy-input-unit-number"
        style={{
          borderRadius: 10, 
          paddingLeft: md ? 0 : theme.spacing(5),
          paddingRight: md ? theme.spacing(1) : 0,
          width: md ? 120 : '100%',
        }}
        onChange={(e) => setUnitValue(e.target.value)}
      />

      {
        typeof props.renderButton === 'function'
          ? props.renderButton({
            'aria-label': 'submit',
            type: 'submit',
            className: cx(classes.button, classNames.submitButton),
            disabled: !home?.place_id,
          }) : (
            <Button
              data-test="fancy-location-search-submit"
              type="submit"
              className={cx(classes.button, classNames.submitButton)}
              disabled={!home?.place_id}
              aria-label="submit"
              variant="contained"
              color="secondary"
            >
              <IconFullArrowRight className={classes.icon} />
            </Button>
          )
      }
    </form>
  );
};

FancyLocationSearch.defaultProps = {
  id: 'fancy-location-search',
  style: {},
  autocompleteRequestOptions: {},
  classes: {},
};

export default FancyLocationSearch;
